import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '@/lib/utils/cn';

const Tabs = TabsPrimitive.Root;

/*
  Dos variantes (spec 310 §5.1):
  - `segmentado` (por omisión): la de siempre, pastillas sobre fondo gris, para
    filtros y modos dentro de una sección.
  - `linea`: subrayado bajo la pestaña activa, para navegar las secciones de
    una página (las de Compite). Se elige en `TabsList`; las pestañas la heredan.
*/
type VarianteDePestanas = 'segmentado' | 'linea';

const VarianteContext = React.createContext<VarianteDePestanas>('segmentado');

const LISTA: Record<VarianteDePestanas, string> = {
  segmentado:
    'inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground',
  linea: 'flex items-center gap-8 overflow-x-auto overflow-y-hidden border-b text-muted-foreground',
};

const PESTANA: Record<VarianteDePestanas, string> = {
  segmentado:
    'rounded-sm px-3 py-1.5 data-[state=active]:bg-campo data-[state=active]:text-foreground data-[state=active]:shadow-sm',
  linea:
    // Como en los bocetos: 15 px, subrayado de 3 px y lugar para un contador. El
    // subrayado, magenta como en las capturas de Melanie (25-oct).
    '-mb-px h-12 gap-2 border-b-[3px] border-transparent px-0.5 text-[15px] hover:text-foreground data-[state=active]:border-prixia-sky data-[state=active]:font-semibold data-[state=active]:text-foreground',
};

const TabsList = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & { variante?: VarianteDePestanas }
>(({ className, variante = 'segmentado', ...props }, ref) => (
  <VarianteContext.Provider value={variante}>
    <TabsPrimitive.List
      ref={ref}
      data-variante={variante}
      className={cn(LISTA[variante], className)}
      {...props}
    />
  </VarianteContext.Provider>
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const variante = React.useContext(VarianteContext);
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-[background-color,color,box-shadow] duration-snap ease-snap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
        PESTANA[variante],
        className
      )}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
export type { VarianteDePestanas };
